// Sound effects using Web Audio API (no audio files needed!)

class SoundManager {
  private audioContext: AudioContext | null = null

  constructor() {
    // Initialize audio context on first user interaction
    if (typeof window !== "undefined") {
      this.initAudioContext()
    }
  }

  private initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)()
    } catch (e) {
      console.warn("Web Audio API not supported")
    }
  }

  private ensureAudioContext() {
    if (!this.audioContext) {
      this.initAudioContext()
    }
    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume()
    }
  }

  // Create a "pew pew" laser sound
  playShootSound() {
    this.ensureAudioContext()
    if (!this.audioContext) return

    const oscillator = this.audioContext.createOscillator()
    const gainNode = this.audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(this.audioContext.destination)

    // High-pitched, short laser sound
    oscillator.type = "sine"
    oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime)
    oscillator.frequency.exponentialRampToValueAtTime(
      400,
      this.audioContext.currentTime + 0.1,
    )

    // Quick fade out
    gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      this.audioContext.currentTime + 0.1,
    )

    oscillator.start(this.audioContext.currentTime)
    oscillator.stop(this.audioContext.currentTime + 0.1)
  }

  // Create a hit/explosion sound
  playHitSound() {
    this.ensureAudioContext()
    if (!this.audioContext) return

    const oscillator = this.audioContext.createOscillator()
    const gainNode = this.audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(this.audioContext.destination)

    // Lower pitched, quick burst
    oscillator.type = "sawtooth"
    oscillator.frequency.setValueAtTime(200, this.audioContext.currentTime)
    oscillator.frequency.exponentialRampToValueAtTime(
      100,
      this.audioContext.currentTime + 0.15,
    )

    // Quick fade out
    gainNode.gain.setValueAtTime(0.2, this.audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      this.audioContext.currentTime + 0.15,
    )

    oscillator.start(this.audioContext.currentTime)
    oscillator.stop(this.audioContext.currentTime + 0.15)
  }

  // Create a player hit/damage sound
  playPlayerHitSound() {
    this.ensureAudioContext()
    if (!this.audioContext) return

    const oscillator = this.audioContext.createOscillator()
    const gainNode = this.audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(this.audioContext.destination)

    // Harsh, alarming sound for player damage
    oscillator.type = "square"
    oscillator.frequency.setValueAtTime(150, this.audioContext.currentTime)
    oscillator.frequency.exponentialRampToValueAtTime(
      80,
      this.audioContext.currentTime + 0.2,
    )

    // Quick fade out
    gainNode.gain.setValueAtTime(0.4, this.audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      this.audioContext.currentTime + 0.2,
    )

    oscillator.start(this.audioContext.currentTime)
    oscillator.stop(this.audioContext.currentTime + 0.2)
  }

  // Create an enemy shooting sound (different from player)
  playEnemyShootSound() {
    this.ensureAudioContext()
    if (!this.audioContext) return

    const oscillator = this.audioContext.createOscillator()
    const gainNode = this.audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(this.audioContext.destination)

    // Lower-pitched, more menacing enemy laser sound
    oscillator.type = "triangle"
    oscillator.frequency.setValueAtTime(300, this.audioContext.currentTime)
    oscillator.frequency.exponentialRampToValueAtTime(
      200,
      this.audioContext.currentTime + 0.12,
    )

    // Quick fade out
    gainNode.gain.setValueAtTime(0.25, this.audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      this.audioContext.currentTime + 0.12,
    )

    oscillator.start(this.audioContext.currentTime)
    oscillator.stop(this.audioContext.currentTime + 0.12)
  }
}

// Export a singleton instance
export const soundManager = new SoundManager()
